import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, User, Lock } from 'lucide-react';
import { PasswordStrengthMeter } from '@/components/PasswordStrengthMeter';

const API = '/api';

export default function ProfileDialog({ open, onOpenChange, forcePasswordTab = false }) {
  const { user, checkAuth } = useAuth();
  const [tab, setTab] = useState(forcePasswordTab ? 'password' : 'info');
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState({ name: '', email: '' });
  const [pw, setPw] = useState({ current_password: '', new_password: '', confirm_password: '' });

  useEffect(() => {
    if (open && user) {
      setInfo({ name: user.name || '', email: user.email || '' });
      setPw({ current_password: '', new_password: '', confirm_password: '' });
      if (forcePasswordTab) setTab('password');
    }
  }, [open, user, forcePasswordTab]);

  const handleUpdateInfo = async () => {
    if (!info.name.trim()) { toast.error('Name is required'); return; }
    if (!info.email.trim()) { toast.error('Email is required'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/auth/profile`, info, { withCredentials: true });
      toast.success('Profile updated');
      checkAuth();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!pw.current_password) { toast.error('Enter current password'); return; }
    if (!pw.new_password) { toast.error('Enter new password'); return; }
    if (pw.new_password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (pw.new_password !== pw.confirm_password) { toast.error('Passwords do not match'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: pw.current_password,
        new_password: pw.new_password,
      }, { withCredentials: true });
      toast.success('Password changed successfully');
      setPw({ current_password: '', new_password: '', confirm_password: '' });
      onOpenChange(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === 'object' && detail.errors) {
        detail.errors.forEach(err => toast.error(err));
      } else {
        toast.error(detail || 'Failed to change password');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px] max-h-[90vh] overflow-y-auto" data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle>My Profile</DialogTitle>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full">
            <TabsTrigger value="info" className="flex-1 gap-1.5"><User className="h-3.5 w-3.5" /> Info</TabsTrigger>
            <TabsTrigger value="password" className="flex-1 gap-1.5"><Lock className="h-3.5 w-3.5" /> Password</TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={info.name}
                onChange={e => setInfo(p => ({ ...p, name: e.target.value }))}
                data-testid="profile-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={info.email}
                onChange={e => setInfo(p => ({ ...p, email: e.target.value }))}
                data-testid="profile-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400">Role</Label>
              <Input value={user?.role || ''} disabled className="bg-slate-50" />
            </div>
            <Separator />
            <Button onClick={handleUpdateInfo} disabled={saving} className="w-full" data-testid="save-profile-btn">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save Changes
            </Button>
          </TabsContent>

          <TabsContent value="password" className="space-y-4 pt-2">
            {forcePasswordTab && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800" data-testid="force-password-change-notice">
                <Lock className="inline h-4 w-4 mr-1" />
                You must change your password before continuing.
              </div>
            )}
            <div className="space-y-2">
              <Label>Current Password</Label>
              <Input
                type="password"
                value={pw.current_password}
                onChange={e => setPw(p => ({ ...p, current_password: e.target.value }))}
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={pw.new_password}
                onChange={e => setPw(p => ({ ...p, new_password: e.target.value }))}
                data-testid="new-password-input"
              />
              <PasswordStrengthMeter password={pw.new_password} />
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                value={pw.confirm_password}
                onChange={e => setPw(p => ({ ...p, confirm_password: e.target.value }))}
                data-testid="confirm-password-input"
              />
              {pw.confirm_password && pw.new_password !== pw.confirm_password && (
                <p className="text-xs text-red-500">Passwords do not match</p>
              )}
            </div>
            <Separator />
            <Button onClick={handleChangePassword} disabled={saving} className="w-full" data-testid="change-password-btn">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Change Password
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
